import React, { useEffect, useMemo, useRef, useState } from "react";
import "./Mid-Section-Mobile.scss";

// import components
import DropdownSelect from "../../../assets/reusableComponents/dropDownSelect";
import CreateExpenseModal from "../Pc-and-Large-Screens/CreateExpendeModal.jsx";
import { ToastContainer, Bounce } from "react-toastify";
import customToasts from "../../../assets/reusableComponents/myToasts";

// import icons
import { ReactComponent as CalenderIcon } from "../../../assets/icons/custom-calender-icon.svg";
import { IoMdAdd } from "react-icons/io";
import { MdOutlinePlumbing } from "react-icons/md";
import { BsLightningFill } from "react-icons/bs";
import { AiFillFormatPainter } from "react-icons/ai";

// redux imports
import { useSelector, useDispatch } from "react-redux";
import { getAllExpensesFromDate } from "../../../../redux/slices/sliceActions";

const expenseTypeChoices = [
  "All",
  "Plumbing",
  "Electricity",
  "Painting",
  "Sewer",
  "Water",
  "Others",
];

const expenseTypeMap = {
  Plumbing: "plumbing",
  Electricity: "electricity",
  Painting: "painting",
  Sewer: "sewer",
  Water: "water",
  Others: "other",
};

const MidSectionExpensesMobile = () => {
  // redux methods
  const dispatch = useDispatch();
  const { expenses, properties } = useSelector((state) => state.landlordslice);

  // modal and toast variables
  const [showModal, setShowModal] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastType, setToastType] = useState("success");

  useEffect(() => {
    if (!showToast) {
      return;
    }

    if (toastType === "success") {
      customToasts.success("Expense created successfully");
    } else if (toastType === "warning") {
      customToasts.warning("Please fill in all fields");
    } else {
      customToasts.error("Error creating expense");
    }

    setShowToast(false);
  }, [showToast, toastType]);

  // date variables
  const getCurrentDate = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return "Select date";
    }

    const day = date.getDate();
    const month = date.toLocaleString("default", { month: "short" });
    const year = date.getFullYear();

    const daySuffix = (dayNumber) => {
      if (dayNumber > 3 && dayNumber < 21) {
        return "th";
      }

      switch (dayNumber % 10) {
        case 1:
          return "st";
        case 2:
          return "nd";
        case 3:
          return "rd";
        default:
          return "th";
      }
    };

    return `${day}${daySuffix(day)} ${month} ${year}`;
  };

  const [startDate, setStartDate] = useState(getCurrentDate());
  const [endDate, setEndDate] = useState(getCurrentDate());
  const startDateRef = useRef(null);
  const endDateRef = useRef(null);

  // apartment variables
  const [propertyChoices, setPropertyChoices] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState("Select Apartment");

  useEffect(() => {
    if (properties.length > 0) {
      const nextChoices = properties.map((property) => property.name);
      setPropertyChoices(nextChoices);

      if (
        selectedProperty === "Select Apartment" ||
        !nextChoices.includes(selectedProperty)
      ) {
        setSelectedProperty(nextChoices[0]);
      }
    }
  }, [properties, selectedProperty]);

  // expense variables
  const [selectedExpenseType, setSelectedExpenseType] = useState(
    expenseTypeChoices[0],
  );

  // fetch expenses based on date and apartment selection
  useEffect(() => {
    if (!selectedProperty || selectedProperty === "Select Apartment") {
      return;
    }

    const selectedPropertyObj = properties.find(
      (property) => property.name === selectedProperty,
    );

    if (!selectedPropertyObj?._id) {
      return;
    }

    const selectedDate = new Date(startDate);
    const selectedMonth = selectedDate.getMonth() + 1;
    const selectedYear = selectedDate.getFullYear();

    if (!selectedMonth || !selectedYear) {
      return;
    }

    dispatch(
      getAllExpensesFromDate({
        property_id: selectedPropertyObj._id,
        month: selectedMonth,
        year: selectedYear,
      }),
    );
  }, [
    dispatch,
    properties,
    selectedProperty,
    startDate,
    endDate,
    selectedExpenseType,
  ]);

  const filteredExpenses = useMemo(() => {
    const startDateObj = new Date(startDate);
    const endDateObj = new Date(endDate);

    const hasValidStartDate = !Number.isNaN(startDateObj.getTime());
    const hasValidEndDate = !Number.isNaN(endDateObj.getTime());

    const normalizedSelectedType =
      selectedExpenseType === "All"
        ? "all"
        : expenseTypeMap[selectedExpenseType] ||
          selectedExpenseType.toLowerCase();

    return expenses.filter((expense) => {
      const expenseDateObj = new Date(expense.date);
      const hasValidExpenseDate = !Number.isNaN(expenseDateObj.getTime());

      const dateInRange =
        !hasValidExpenseDate ||
        ((!hasValidStartDate || expenseDateObj >= startDateObj) &&
          (!hasValidEndDate || expenseDateObj <= endDateObj));

      const typeMatches =
        normalizedSelectedType === "all" ||
        expense.type === normalizedSelectedType;

      const propertyMatches =
        selectedProperty === "Select Apartment" ||
        expense.property?.name === selectedProperty;

      return dateInRange && typeMatches && propertyMatches;
    });
  }, [expenses, selectedExpenseType, selectedProperty, startDate, endDate]);

  const [expandedExpenseId, setExpandedExpenseId] = useState(null);

  const handleToggleCard = (expenseId) => {
    setExpandedExpenseId((previousId) =>
      previousId === expenseId ? null : expenseId,
    );
  };

  const openDatePicker = (ref) => {
    if (!ref?.current) {
      return;
    }

    ref.current.focus();
    if (typeof ref.current.showPicker === "function") {
      ref.current.showPicker();
    }
  };

  const formatExpenseType = (typeValue) => {
    if (!typeValue) {
      return "Unknown";
    }
    return typeValue.charAt(0).toUpperCase() + typeValue.slice(1);
  };

  const formatCurrency = (amount) => {
    const amountAsNumber = Number(amount);
    if (Number.isNaN(amountAsNumber)) {
      return amount;
    }
    return amountAsNumber.toLocaleString();
  };

  const getExpenseIcon = (expenseType) => {
    switch (expenseType) {
      case "electricity":
        return <BsLightningFill size={18} />;
      case "painting":
        return <AiFillFormatPainter size={18} />;
      case "plumbing":
      case "sewer":
      case "water":
      case "other":
      default:
        return <MdOutlinePlumbing size={18} />;
    }
  };

  return (
    <div className="mid-section-expenses-mobile">
      <div className="title-section">
        <div className="title-and-description">
          <div className="title">Expenses</div>
          <div className="description">Track expenses on properties</div>
        </div>
        <button
          type="button"
          className="create-expense-button"
          onClick={() => setShowModal(true)}
        >
          <IoMdAdd />
          Create New
        </button>
      </div>

      <div className="filters-card">
        <div className="filters-header">All Expenses</div>
        <div className="filters-grid">
          <div className="filter-item">
            <div className="filter-title">Start Date</div>
            <div
              className="date-shell"
              onClick={() => openDatePicker(startDateRef)}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  openDatePicker(startDateRef);
                }
              }}
            >
              <input
                className="native-date-input"
                type="date"
                value={startDate}
                onChange={(event) => setStartDate(event.target.value)}
                ref={startDateRef}
              />
              <div className="date-text">{formatDate(startDate)}</div>
              <CalenderIcon className="date-icon" />
            </div>
          </div>

          <div className="filter-item">
            <div className="filter-title">End Date</div>
            <div
              className="date-shell"
              onClick={() => openDatePicker(endDateRef)}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  openDatePicker(endDateRef);
                }
              }}
            >
              <input
                className="native-date-input"
                type="date"
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
                ref={endDateRef}
              />
              <div className="date-text">{formatDate(endDate)}</div>
              <CalenderIcon className="date-icon" />
            </div>
          </div>

          <div className="filter-item">
            <div className="filter-title">Apartment Name</div>
            <DropdownSelect
              choices={propertyChoices}
              defaultChoice={selectedProperty}
              onSelect={setSelectedProperty}
              backgroundColor="#ffffff"
              textColor="#192A3A"
              border="1px solid #dbe3ec"
              padding="10px"
              borderRadius="8px"
            />
          </div>

          <div className="filter-item">
            <div className="filter-title">Expense Type</div>
            <DropdownSelect
              choices={expenseTypeChoices}
              defaultChoice={selectedExpenseType}
              onSelect={setSelectedExpenseType}
              backgroundColor="#ffffff"
              textColor="#192A3A"
              border="1px solid #dbe3ec"
              padding="10px"
              borderRadius="8px"
            />
          </div>
        </div>
        <div className="filters-note">
          Apply filters to view specific expenses
        </div>
      </div>

      <div className="history-title">Expenses History</div>

      {filteredExpenses.length === 0 ? (
        <div className="empty-state">
          No expenses found for the selected filters.
        </div>
      ) : (
        <div className="expenses-list">
          {filteredExpenses.map((expense, index) => {
            const expenseId = expense._id || `${expense.type}-${index}`;
            const isExpanded = expandedExpenseId === expenseId;

            return (
              <div className="expense-card" key={expenseId}>
                <button
                  type="button"
                  className="expense-main"
                  onClick={() => handleToggleCard(expenseId)}
                >
                  <div className="left-side">
                    <div className="icon-shell">
                      {getExpenseIcon(expense.type)}
                    </div>
                    <div className="copy-block">
                      <div className="expense-type">
                        {formatExpenseType(expense.type)}
                      </div>
                      <div className="expense-date">
                        {new Date(expense.date).toLocaleDateString("default", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </div>
                    </div>
                  </div>
                  <div className="amount">
                    Ksh {formatCurrency(expense.amount)}
                  </div>
                </button>

                {isExpanded && (
                  <div className="expense-details">
                    <div className="detail-row">
                      <span className="detail-label">Apartment</span>
                      <span className="detail-value">
                        {expense.property?.name || "N/A"}
                      </span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">House</span>
                      <span className="detail-value">
                        {expense.house?.house_no || "N/A"}
                      </span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Description</span>
                      <span className="detail-value">
                        {expense.description || "No description"}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <CreateExpenseModal
          setShowModal={setShowModal}
          setShowToast={setShowToast}
          setToastType={setToastType}
        />
      )}

      <ToastContainer
        position="bottom-left"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        transition={Bounce}
      />
    </div>
  );
};

export default MidSectionExpensesMobile;
